<template>
  <div class="Spam">
    <div class="Spam-header">
      <h1 class="Spam-title">Spam Detection</h1>
      <button
        class="Button Spam-refreshButton"
        @click="loadSpamEvents"
        :disabled="loading">
        <md-icon>refresh</md-icon>
        Refresh
      </button>
    </div>

    <div v-if="loading" class="Spam-loading">
      <div class="Spam-spinner"></div>
      <p>Loading spam events...</p>
    </div>

    <div v-if="error" class="Spam-error Error">
      <p>{{ error }}</p>
    </div>

    <div v-if="success" class="Spam-success">
      <p>{{ success }}</p>
    </div>

    <div class="FlexTable" v-if="spamEventsData.length > 0">
      <div class="FlexTable-header">
        <h3 class="FlexTable-title">
          {{ spamEventsData.length }} potential spam event<span v-if="spamEventsData.length > 1">s</span> found
        </h3>
      </div>
      <ve-table
        class="Spam-table"
        row-key-field-name="id"
        :fixed-header="true"
        :columns="tableColumns"
        :table-data="spamEventsData"
        :cell-style-option="cellStyleOption"
        :rowStyleOption="rowStyleOption"
        :expand-option="expandOption"
        :clipboard-option="{copy:true}"
      />
    </div>

    <div v-else-if="!loading" class="FlexTable">
      <div class="FlexTable-header">
        <h3 class="FlexTable-title">No spam events found</h3>
      </div>
      <p class="FlexTable-content FlexTable-contentItem">
        No potential spam events detected at this time.
      </p>
    </div>

    <!-- Confirmation Dialog -->
    <div v-if="showConfirmation" class="Spam-confirmation Card Card--top">
      <div class="Card-header">
        <h3 class="Card-title">Confirm Spam Marking</h3>
      </div>
      <div class="Card-body">
        <p>Are you sure you want to mark this event as spam?</p>
        <p><strong>Event:</strong> {{ selectedEvent ? selectedEvent.name : '' }}</p>
        <p><strong>Event ID:</strong> {{ selectedEvent ? selectedEvent.id : '' }}</p>
        <p>This action cannot be undone.</p>
        <div class="Spam-confirmationActions">
          <button
            class="Button"
            @click="confirmMarkAsSpam">
            Confirm
          </button>
          <button
            class="Button Button--secondary"
            @click="cancelMarkAsSpam">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './Spam.scss';
import axios from "axios";
import errorModel from '../../models/error';
import cookieModel from '../../models/cookie';
import $router from '../../router';

export default {
  name: 'Spam',
  mounted() {
    this.loadSpamEvents();
  },
  methods: {
    loadSpamEvents: function() {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      const url = vm.baseUrl + "/admin/spam/search";

      vm.axiosInstance.get(url)
        .then(function (response) {
          if (response.data && response.data.success) {
            vm.spamEvents = response.data.data || [];
            vm.spamEventsData = response.data.data || [];
            vm.totalFound = response.data.total_found;
            vm.loading = false;
          } else {
            vm.error = response.data.message || 'Failed to load spam events';
            vm.loading = false;
          }
        }).catch(function (error) {
          console.error('API error:', error);
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },

    markAsSpam: function(event) {
      this.selectedEvent = event;
      this.showConfirmation = true;
    },

    confirmMarkAsSpam: function() {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      const postData = {
        event_id: vm.selectedEvent.id
      };

      vm.axiosInstance.post(vm.baseUrl + "/admin/spam/mark", postData)
        .then(function (response) {
          if (response.data && response.data.success) {
            vm.success = response.data.message;
            // Update the event status in the local data
            const eventIndex = vm.spamEvents.findIndex(e => e.id === vm.selectedEvent.id);
            if (eventIndex !== -1) {
              vm.spamEvents[eventIndex].status = 7;
              vm.spamEvents[eventIndex].status_type = 'SPAM';
            }
            vm.showConfirmation = false;
            vm.selectedEvent = null;
            vm.loading = false;
          } else {
            vm.error = response.data.message || 'Failed to mark event as spam';
            vm.loading = false;
          }
        }).catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },

    cancelMarkAsSpam: function() {
      this.showConfirmation = false;
      this.selectedEvent = null;
    },

    getSpamScoreClass: function(score) {
      if (score >= 7) return 'Spam-highScore';
      if (score >= 5) return 'Spam-mediumScore';
      return 'Spam-lowScore';
    },

    viewEventsByOrganiser: function(userId) {
      // Navigate to search page with user ID search
      $router.push({
        path: '/search',
        query: {
          search: userId,
          searchType: 'event'
        }
      });
    },

    searchRelatedEvents: function(event) {
      // Extract key terms from the event name for searching
      const searchTerms = this.extractSearchTerms(event.user_email);
      
      // Navigate to search page with the search terms
      $router.push({
        path: '/search',
        query: {
          search: searchTerms,
          searchType: 'event'
        }
      });
    },

    extractSearchTerms: function(eventName) {
      // Extract key terms that might be used in other spam events
      const terms = eventName.toLowerCase()
        .replace(/[^\w\s]/g, ' ') // Remove special characters
        .split(/\s+/)
        .filter(term => term.length > 3) // Only keep terms longer than 3 characters
        .slice(0, 3); // Take first 3 terms
      
      return terms.join(' ');
    },

    blockUser: function(event) {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + event.user_id + "/block")
        .then(function (response) {
          if (response.data && response.data.success) {
            vm.success = response.data.message;
            // Update the user status in the local data
            const eventIndex = vm.spamEvents.findIndex(e => e.id === event.id);
            if (eventIndex !== -1) {
              vm.spamEvents[eventIndex].user_status = 'Blocked';
            }
            vm.loading = false;
          } else {
            vm.error = response.data.message || 'Failed to block user';
            vm.loading = false;
          }
        }).catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },

    blockUserAndMarkSpam: function(event) {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + event.user_id + "/block-and-mark-spam")
        .then(function (response) {
          if (response.data && response.data.success) {
            vm.success = response.data.message;
            // Update the user status and event status in the local data
            const eventIndex = vm.spamEvents.findIndex(e => e.id === event.id);
            if (eventIndex !== -1) {
              vm.spamEvents[eventIndex].user_status = 'Blocked';
              vm.spamEvents[eventIndex].status = 7;
              vm.spamEvents[eventIndex].status_type = 'SPAM';
            }
            vm.loading = false;
          } else {
            vm.error = response.data.message || 'Failed to block user and mark events as spam';
            vm.loading = false;
          }
        }).catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },
    trustUser: function(event) {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + event.user_id + "/trust")
        .then(function (response) {
          if (response.data && response.data.success) {
            vm.success = response.data.message;
            // Update the user type in the local data
            const eventIndex = vm.spamEvents.findIndex(e => e.id === event.id);
            if (eventIndex !== -1) {
              vm.spamEvents[eventIndex].user_type = 2;
            }
            vm.loading = false;
          } else {
            vm.error = response.data.message || 'Failed to trust user';
            vm.loading = false;
          }
        }).catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    }
  },
        data: function () {
        return {
          baseUrl: process.env.VUE_APP_API_URL || 'https://api.ticketlab.co.uk/api',
          axiosInstance: axios.create({
            baseURL: process.env.VUE_APP_API_URL || 'https://api.ticketlab.co.uk/api',
          }),
          loading: false,
          error: '',
          success: '',
          spamEvents: [],
          spamEventsData: [],
          totalFound: 0,
          showConfirmation: false,
          selectedEvent: null,
      tableColumns: [
        {
          field: "id",
          key: "id",
          title: "Event ID",
          align: "right",
          operationColumn: true,
          type: "expand",
        },
        {
          field: "name",
          key: "name",
          title: "Event Name",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
              <span>
                <a href={row.url} target="_blank" class="Spam-eventLink">
                  {row.name}
                </a>
              </span>
            );
          },
        },
        {
          field: "spam_score",
          key: "spamScore",
          title: "Spam Score",
          align: "center",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const scoreClass = this.getSpamScoreClass(row.spam_score);
            return (
              <span class={scoreClass}>
                {row.spam_score}
              </span>
            );
          },
        },
        {
          field: "status_type",
          key: "statusType",
          title: "Status",
          align: "left",
        },
        {
          field: "created",
          key: "created",
          title: "Created",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const date = new Date(row.created).toLocaleDateString('en-GB');
            return (
              <span>
                {date}
              </span>
            );
          },
        },
        {
          field: "matched_patterns",
          key: "matchedPatterns",
          title: "Matched Patterns",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const patterns = Array.isArray(row.matched_patterns) ? row.matched_patterns : 
                           (typeof row.matched_patterns === 'string' ? [row.matched_patterns] : []);
            return (
              <span>
                {patterns.length > 0 ? patterns.join(', ') : 'None'}
              </span>
            );
          },
        },
        {
          field: "",
          key: "actions",
          title: "Actions",
          width: "200px",
          align: "center",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const isSpam = row.status === 7 || row.status_type === 'SPAM';
            return (
              <span>
                <button
                  class="Button"
                  disabled={isSpam}
                  on-click={() => this.markAsSpam(row)}>
                  {isSpam ? 'Marked as Spam' : 'Mark as Spam'}
                </button>
                <a
                  href={row.url}
                  target="_blank"
                  class="Button Button--secondary">
                  View Event
                </a>
                <button
                  class="Button Button--secondary"
                  on-click={() => this.searchRelatedEvents(row)}>
                  View Related Events
                </button>
                {row.user_id && (
                  <button
                    class="Button Button--warning"
                    disabled={row.user_status === 'Blocked'}
                    on-click={() => this.blockUser(row)}>
                    {row.user_status === 'Blocked' ? 'User Blocked' : 'Block User'}
                  </button>
                )}
                {row.user_id && (
                  <button
                    class="Button Button--danger"
                    disabled={row.user_status === 'Blocked'}
                    on-click={() => this.blockUserAndMarkSpam(row)}>
                    {row.user_status === 'Blocked' ? 'User Blocked' : 'Block & Mark Events as Spam'}
                  </button>
                )}
                {row.user_id && (
                  <button
                    class="Button Button--info"
                    on-click={() => this.trustUser(row)}>
                    Trust User
                  </button>
                )}
              </span>
            );
          },
        },
      ],
      cellStyleOption: {
        bodyCellClass: ({row, column}) => {
          if (column.field === "spam_score") {
            return this.getSpamScoreClass(row.spam_score);
          }
        },
      },
      rowStyleOption: {
        clickHighlight: true,
        hoverHighlight: false,
      },
      expandOption: {
        /* eslint-disable no-unused-vars */ // h is required for render to work
        render: ({row}, h) => {
          const patterns = Array.isArray(row.matched_patterns) ? row.matched_patterns : 
                         (typeof row.matched_patterns === 'string' ? [row.matched_patterns] : []);
          return (
            <div class="Spam-expandContent">
              <h4>Event Details</h4>
              <p><strong>Description:</strong> {row.description || 'No description available'}</p>
              <p><strong>Matched Patterns:</strong> {patterns.length > 0 ? patterns.join(', ') : 'None'}</p>
              <p><strong>Spam Score:</strong> {row.spam_score}</p>
              <p><strong>Event URL:</strong> <a href={row.url} target="_blank">{row.url}</a></p>
            </div>
          );
        },
      },
    }
  }
}
</script> 